import React, {useEffect, useState} from 'react';
import {Alert, Button, Card, Col, Form, Input, message, Row} from "antd";
import {calculateTimeDifference} from "../../helpers/time";
import { useTranslation } from '../../hooks/useTranslation';
import { useIsPc } from '../../hooks/useIsPc';

type PurchaseSlot = 'ONE_DAY' | 'ONE_WEEK' | 'ONE_MONTH' | 'ONE_YEAR';

interface PurchaseLink {
  id?: number;
  slot: PurchaseSlot;
  title: string;
  label?: string;
  url: string;
  pcOnly?: boolean;
  sortOrder?: number;
  enabled?: boolean;
}

const Config: React.FC = () => {
  const { t } = useTranslation();
  const isPc = useIsPc();
  const [config, setConfig] = useState<Record<string, string>>({});
  const [userInfo, setUserInfo] = useState<Record<string, string>>({});
  const [cardNo, setCardNo] = useState('');
  const [cardPass, setCardPass] = useState('');
  const [extendCardNo, setExtendCardNo] = useState('');
  const [extendCardPass, setExtendCardPass] = useState('');
  const [purchaseLinks, setPurchaseLinks] = useState<Record<PurchaseSlot, PurchaseLink[]>>({
    ONE_DAY: [],
    ONE_WEEK: [],
    ONE_MONTH: [],
    ONE_YEAR: [],
  });

  const fetchPurchaseLinks = () => {
    fetch('/api/img-cloud/purchase-links')
      .then((res) => res.json())
      .then((res) => {
        if (res.success && res.data) {
          setPurchaseLinks((prev) => ({
            ...prev,
            ...res.data,
          }));
        }
      })
      .catch(() => {
        // 出错时使用默认配置兜底（当前硬编码逻辑）
      });
  };

  const updateUserInfo = () => {
    fetch('/login/info', {
      method: 'GET',
    }).then(res => res.json()).then((e) => {
      console.info(e)
      if (e.isSuccess) {
        setConfig(JSON.parse(e.detail?.config || '{}'));
        console.info('data is ', e.detail);
        setUserInfo(e.detail);
      }
    })
  }

  useEffect(() => {
    updateUserInfo();
    fetchPurchaseLinks();
  }, [])

  const [saving, setSaving] = useState(false)
  const [extending, setExtending] = useState(false)

  const handleSave = () => {
    console.info('handleSave', config)
    setSaving(true);
    fetch('/login/active', {
      method: 'POST',
      body: JSON.stringify({
        cardNo,
        cardPass
      }),
      headers: {
        'Content-Type': 'application/json;charset=utf8'
      }
    }).then(res => res.json()).then((data) => {
      console.info('flag is', data);
      if (data.success || data.isSuccess) {
        message.info(data.message || t('cart.activateSuccess'))
        updateUserInfo();
        setCardNo('');
        setCardPass('');
      } else {
        message.error(data.message || t('cart.activateFailed'))
      }
    }).finally(() => {
      setSaving(false);
    })
  }

  const handleExtend = () => {
    if (!extendCardNo.trim()) {
      message.error(t('cart.cardNoRequired'));
      return;
    }
    
    setExtending(true);
    fetch('/login/active', {
      method: 'POST',
      body: JSON.stringify({
        cardNo: extendCardNo,
        cardPass: extendCardPass
      }),
      headers: {
        'Content-Type': 'application/json;charset=utf8'
      }
    }).then(res => res.json()).then((data) => {
      console.info('extend result:', data);
      if (data.success || data.isSuccess) {
        message.success(data.message || t('cart.renewalSuccess'));
        updateUserInfo();
        setExtendCardNo('');
        setExtendCardPass('');
      } else {
        message.error(data.message || t('cart.renewalFailed'))
      }
    }).finally(() => {
      setExtending(false);
    })
  }

  console.info('user is', userInfo);
  if (userInfo.isActiveVip) {
    // vipEndTime. 格式为 YYYY-MM-DD HH:mm:ss
    const ttl = calculateTimeDifference(userInfo.vipEndTime || '');
    const text = t('cart.vipActive', { endTime: userInfo.vipEndTime || '-', ttl });
    return (
      <>
        <Alert message={text} type="success" style={{ marginBottom: 16 }}/>
        <Card title={t('cart.vipRenewal')} style={{ marginTop: 16 }}>
          <Alert 
            type="info" 
            message={t('cart.renewalHint')}
            style={{ marginBottom: 16 }}
          />
          <Form.Item label={t('cart.cardNo')}>
            <Input 
              placeholder={t('cart.cardNoPlaceholder')} 
              value={extendCardNo}
              onChange={(e) => setExtendCardNo(e.target.value)}
            />
          </Form.Item>
          <Form.Item label={t('cart.password')}>
            <Input 
              placeholder={t('cart.passwordPlaceholder')} 
              value={extendCardPass}
              onChange={(e) => setExtendCardPass(e.target.value)}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" loading={extending} onClick={handleExtend}>
              {t('cart.renewalMember')}
            </Button>
          </Form.Item>
        </Card>
      </>
    )
  }

  const renderLinks = (slot: PurchaseSlot) => {
    const links = purchaseLinks[slot] || [];
    if (!links.length) {
      return null;
    }

    return links
      .filter((link) => !link.pcOnly || isPc)
      .map((link, index) => {
        const prefix = link.label || `${index + 1}. `;
        const style: React.CSSProperties = {};
        if (index === 0 && isPc) {
          style.color = 'red';
        }
        return (
          <div key={link.id || `${slot}-${index}`}>
            <a
              href={link.url}
              target="_blank"
              rel="noreferrer"
              style={style}
            >
              {prefix}{link.title}
            </a>
          </div>
        );
      });
  };

  return (
    <>
      <Form>
        <Row gutter={16}>
          <Col span={8}>
            <Card title={t('cart.oneDayMember')}>
              {renderLinks('ONE_DAY')}
            </Card>
            <Card title={t('cart.xianyuStore')}>
              {renderLinks('ONE_DAY')}
            </Card>
          </Col>
          <Col span={8}>
            <Card title={t('cart.oneMonthMember')}>
              {renderLinks('ONE_MONTH')}
            </Card>
            <br/>
            <Card title={t('cart.oneWeekMember')}>
              {renderLinks('ONE_WEEK')}
            </Card>
            <Card title={t('cart.oneYearMember')}>
              {renderLinks('ONE_YEAR')}
              <img style={{width: "150px"}} src={"https://cdn.z.wiki/autoupload/20241217/W2xb/630X678/image.png"}/>
            </Card>
          </Col>
          <Col span={8}>
            <Card title={t('cart.afterPurchase')}>
              <a target={"_blank"}
                 rel="noreferrer"
                 href={"https://cdn.z.wiki/autoupload/20241017/9UVf/744X1614/image.png"}>
                {t('cart.clickToEnlarge')}
                <img src={"https://cdn.z.wiki/autoupload/20241017/9UVf/744X1614/image.png"}
                     alt={t('cart.cardPasswordGuide')}
                     style={{maxWidth: '50%', margin: '0 auto'}}/>
              </a>
            </Card>
          </Col>
        </Row>
        <br/>
        <Alert type={'info'} message={t('cart.activateHint')}/>
        <br/>
        <Form.Item label={t('cart.cardNo')}>
          <Input placeholder={t('cart.cardNoPlaceholder')} value={cardNo}
                 onChange={(e) => setCardNo(e.target.value)}/>
        </Form.Item>
        <Form.Item label={t('cart.password')}>
          <Input placeholder={t('cart.passwordPlaceholder')} value={cardPass}
                 onChange={(e) => setCardPass(e.target.value)}/>
        </Form.Item>
        <Form.Item>
          <Button type="primary" loading={saving} onClick={handleSave}>{t('cart.activateMember')}</Button>
        </Form.Item>
      </Form>
    </>
  );
};

export default Config;